import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntervaloVisivel } from '../hooks/useIntervaloVisivel'
import { supabase } from '../supabaseClient'
import { consultarCredenciais, consultarGoodwe, consultarSolplanet } from '../services/goodweMonitoramento'
import MonitoramentoAvisos from './MonitoramentoAvisos'
import Notification from './Notification'
import 'bootstrap-icons/font/bootstrap-icons.css'
import './Monitoramento.css'

const FILTROS = [
  { id: 'todas', label: 'Todos' },
  { id: 'ok', label: 'Normais' },
  { id: 'atencao', label: 'Atenção' },
  { id: 'offline', label: 'Offline' },
  { id: 'falha', label: 'Falhas' }
]

const MARCAS = [
  { id: 'todas', label: 'Todas as marcas' },
  { id: 'GoodWe', label: 'GoodWe' },
  { id: 'Solplanet', label: 'Solplanet' }
]

const FONTES = [
  { marca: 'GoodWe', consultar: consultarGoodwe },
  { marca: 'Solplanet', consultar: consultarSolplanet }
]

function numeroPt(valor, casas = 1) {
  if (valor == null || Number.isNaN(Number(valor))) return '—'
  return Number(valor).toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas
  })
}

function horaCurta(iso) {
  if (!iso) return ''
  const data = new Date(iso)
  if (Number.isNaN(data.getTime())) return ''
  return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

function energiaResumo(kwh) {
  if (kwh == null || Number.isNaN(Number(kwh))) return { valor: '—', unidade: 'kWh' }
  const n = Number(kwh)
  if (Math.abs(n) >= 1000) return { valor: numeroPt(n / 1000, 1), unidade: 'MWh' }
  return { valor: numeroPt(n, 1), unidade: 'kWh' }
}
function horaLocal(iso) {
  if (!iso) return ''
  const data = new Date(iso)
  if (Number.isNaN(data.getTime())) return String(iso)
  return data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

function diaCurto(iso) {
  if (!iso) return ''
  const pedaco = String(iso).slice(0, 10)
  const [, mes, dia] = pedaco.split('-')
  if (!dia || !mes) return pedaco
  return `${dia}/${mes}`
}

function faltaConfiguracao(falha) {
  if (falha?.codigo === 'CREDENCIAIS' || falha?.configurado === false) return true
  const msg = String(falha?.message || '')
  return msg.includes('Não foi possível contactar a Edge Function') || msg.includes('Falha na Edge Function')
}

function textoCredencial(mensagem) {
  const texto = String(mensagem || 'Não foi possível salvar as credenciais.')
  const baixo = texto.toLowerCase()
  if (baixo.includes('function was not found')) {
    return 'A função monitoramento-credenciais ainda não foi publicada no Supabase.'
  }
  if (baixo.includes('permission denied')) {
    return 'Falta liberar a tabela no banco. Execute a migração nova no SQL Editor do Supabase.'
  }
  return texto
}

function explicarFalha(marca, mensagem) {
  const texto = String(mensagem || '')
  const baixo = texto.toLowerCase()
  const funcao = marca === 'Solplanet' ? 'solplanet-monitoramento' : 'goodwe-monitoramento'

  if (baixo.includes('requested function was not found') || baixo.includes('function was not found')) {
    return `A função ${funcao} ainda não foi publicada no Supabase. Sem essa publicação, o sistema não consulta a ${marca}.`
  }
  if (baixo.includes('系统异常') || baixo.includes('sistema anormal')) {
    return 'A Solplanet não conseguiu abrir esta usina. Atualize a função solplanet-monitoramento e tente de novo.'
  }
  if (baixo.includes('invalid appkey') && !texto.includes('foi recusado')) {
    return 'A Solplanet recusou o AppKey. Na configuração da conta, copie só o primeiro valor, o código de autorização. O AppSecret é o outro valor, aberto pelo ícone de olho, sem asteriscos.'
  }
  if (baixo.includes('invalid signature') || (baixo.includes('signature') && baixo.includes('match'))) {
    return 'A Solplanet recusou a assinatura. O AppSecret precisa ser o par do mesmo AppKey, os dois copiados em Definições da conta.'
  }
  return texto
}

function somarCampo(usinas, campo) {
  const valores = usinas.map((usina) => usina[campo]).filter((valor) => valor != null)
  if (!valores.length) return null
  return valores.reduce((total, valor) => total + Number(valor), 0)
}

function textoPendencia(marca, podeCredenciais, admin) {
  if (marca === 'GoodWe') {
    const acao = admin
      ? 'Abra a engrenagem, escolha a empresa e salve o AppId e o AppSecret dessa conta.'
      : podeCredenciais
        ? 'Abra a engrenagem e salve o AppId e o AppSecret da sua empresa.'
        : 'Peça a um administrador para concluir a ligação com a GoodWe.'
    return `Conta GoodWe ainda não ligada. As usinas GoodWe precisam estar na mesma conta organização SEMS. O AppId e o AppSecret ficam só no servidor. ${acao}`
  }
  const acao = admin
    ? 'Abra a engrenagem, escolha a empresa e salve o e-mail e a senha dessa conta.'
    : podeCredenciais
      ? 'Abra a engrenagem e salve o e-mail e a senha da sua empresa.'
      : 'Peça a um administrador para concluir a ligação com a Solplanet.'
  return `Conta Solplanet ainda não ligada. As usinas são as da conta em internation-pro-cloud.solplanet.net. ${acao}`
}

function ajustarStatus(item) {
  if (!item) return item
  const texto = String(item.status || '').trim().toLowerCase()
  if (texto === 'offline' || texto === 'desligado' || texto === 'sem comunicação' || texto === 'sem comunicacao') {
    return { ...item, nivel: 'offline' }
  }
  if (texto === 'falha de comunicação' || texto === 'falha de comunicacao') {
    return { ...item, status: 'Sem comunicação', nivel: 'offline' }
  }
  return item
}

function StatusPill({ nivel, texto }) {
  const tom = nivel === 'ok' ? 'online' : nivel === 'atencao' ? 'warning' : nivel === 'offline' ? 'offline' : nivel === 'falha' ? 'falha' : 'neutro'
  const ponto = nivel === 'ok' ? 'mon-dot-green' : nivel === 'atencao' ? 'mon-dot-yellow' : nivel === 'falha' ? 'mon-dot-red' : 'mon-dot-muted'
  return (
    <span className={`mon-status-pill mon-status-${tom}`}>
      <span className={`mon-dot ${ponto}`} />
      {texto || 'Sem status'}
    </span>
  )
}

function MarcaSelo({ marca }) {
  const goodwe = marca === 'GoodWe'
  return <span className={`mon-marca-selo ${goodwe ? 'is-goodwe' : 'is-solplanet'}`}>{marca}</span>
}

function KpiCard({ rotulo, valor, unidade, icone, tom }) {
  return (
    <div className="col-6 col-md-4 mon-kpi-slot">
      <div className={`mon-kpi-card mon-kpi-${tom}`}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="text-muted small fw-semibold">{rotulo}</span>
          <div className={`mon-kpi-icone mon-kpi-icone-${tom}`}>
            <i className={`bi ${icone}`} aria-hidden />
          </div>
        </div>
        <h3 className="mon-kpi-valor m-0">
          {valor}
          {unidade ? <span> {unidade}</span> : null}
        </h3>
      </div>
    </div>
  )
}

function resumoInversor(inv) {
  const fases = (inv.fases?.tensao || []).filter((valor) => valor != null)
  const extras = [
    fases.length > 0 ? `Fases ${fases.map((valor) => `${numeroPt(valor, 0)} V`).join(' · ')}` : null,
    inv.alarme ? `Código de falha ${inv.alarme}` : null,
    inv.temperatura != null ? `${numeroPt(inv.temperatura)} °C` : null
  ].filter(Boolean)
  return extras
}

function GraficoGeracao({ historico, maxKwh }) {
  if (!historico.length) {
    return <p className="mon-vazio mb-0">Ainda não há histórico desta usina.</p>
  }
  return (
    <div className="mon-grafico" role="img" aria-label="Geração diária em kWh">
      {historico.map((ponto) => {
        const altura = maxKwh > 0 ? Math.max(4, ((Number(ponto.kwh) || 0) / maxKwh) * 100) : 0
        return (
          <div key={ponto.data} className="mon-barra" title={`${diaCurto(ponto.data)}: ${numeroPt(ponto.kwh)} kWh`}>
            <span style={{ height: `${ponto.kwh ? altura : 0}%` }} />
            <small>{diaCurto(ponto.data)}</small>
          </div>
        )
      })}
    </div>
  )
}

export default function Monitoramento({ onBack, userRole, userEmpresa }) {
  const [busca, setBusca] = useState('')
  const [filtro, setFiltro] = useState('todas')
  const [marcaFiltro, setMarcaFiltro] = useState('todas')
  const [lista, setLista] = useState(null)
  const [detalhe, setDetalhe] = useState(null)
  const [selecionada, setSelecionada] = useState(null)
  const [carregando, setCarregando] = useState(true)
  const [erros, setErros] = useState([])
  const [credenciaisAbertas, setCredenciaisAbertas] = useState(false)
  const [avisosAbertos, setAvisosAbertos] = useState(false)
  const [abaCredencial, setAbaCredencial] = useState('solplanet')
  const [credenciais, setCredenciais] = useState({
    solplanet: { appKey: '', appSecret: '', token: '' },
    goodwe: { appId: '', appSecret: '' }
  })
  const [salvandoCredenciais, setSalvandoCredenciais] = useState(false)
  const [avisoCredenciais, setAvisoCredenciais] = useState('')
  const [empresasCredencial, setEmpresasCredencial] = useState([])
  const [empresaCredencial, setEmpresaCredencial] = useState(userEmpresa?.id || '')
  const [pendencias, setPendencias] = useState([])
  const [toast, setToast] = useState(null)
  const pedido = useRef(0)
  const selecionadaRef = useRef(null)
  const pendenciasAvisadas = useRef(new Set())

  const carregarLista = useCallback(async (silencioso = false) => {
    const id = ++pedido.current
    if (!silencioso) {
      setCarregando(true)
      setErros([])
    }
    const resultados = await Promise.all(FONTES.map(async (fonte) => {
      try {
        const dados = await fonte.consultar('usinas')
        return { marca: fonte.marca, dados, falha: null }
      } catch (falha) {
        return { marca: fonte.marca, dados: null, falha }
      }
    }))
    if (pedido.current !== id) return

    const usinas = []
    const avisos = []
    const faltas = []
    const erros = []
    let atualizadoEm = null

    resultados.forEach((resultado) => {
      if (resultado.falha) {
        if (faltaConfiguracao(resultado.falha)) faltas.push(resultado.marca)
        else erros.push({
          marca: resultado.marca,
          texto: explicarFalha(resultado.marca, resultado.falha.message || 'Não foi possível consultar as usinas.')
        })
        return
      }
      const dados = resultado.dados
      if (dados?.aviso) avisos.push(`${resultado.marca}: ${dados.aviso}`)
      if (dados?.atualizadoEm && (!atualizadoEm || dados.atualizadoEm > atualizadoEm)) {
        atualizadoEm = dados.atualizadoEm
      }
      ;(dados?.usinas || []).forEach((usina) => {
        usinas.push(ajustarStatus({ ...usina, marca: resultado.marca }))
      })
    })

    setPendencias(faltas)
    setErros(erros)
    setLista({
      usinas,
      aviso: avisos.join(' '),
      atualizadoEm,
      totais: {
        usinas: usinas.length,
        normais: usinas.filter((usina) => usina.nivel === 'ok').length,
        offline: usinas.filter((usina) => usina.nivel === 'offline').length,
        falhas: usinas.filter((usina) => usina.nivel === 'falha').length,
        geracaoHojeKwh: somarCampo(usinas, 'geracaoHojeKwh'),
        geracaoTotalKwh: somarCampo(usinas, 'geracaoTotalKwh')
      }
    })
    setCarregando(false)
  }, [])

  const carregarDetalhe = useCallback(async (usina, silencioso = false) => {
    const id = ++pedido.current
    if (!silencioso) {
      setCarregando(true)
      setErros([])
    }
    const consultar = usina.marca === 'Solplanet' ? consultarSolplanet : consultarGoodwe
    try {
      const dados = await consultar('detalhe', { stationId: usina.id, empresaId: usina.empresaId || '' })
      if (pedido.current !== id) return
      setDetalhe({
        ...dados,
        usina: ajustarStatus(dados?.usina),
        inversores: (dados?.inversores || []).map(ajustarStatus)
      })
      if (silencioso) setErros([])
    } catch (falha) {
      if (pedido.current !== id) return
      if (faltaConfiguracao(falha)) setPendencias([usina.marca])
      setErros([{
        marca: usina.marca,
        texto: explicarFalha(usina.marca, falha.message || 'Não foi possível abrir a usina.')
      }])
    } finally {
      if (pedido.current === id) setCarregando(false)
    }
  }, [])

  useEffect(() => {
    carregarLista()
  }, [carregarLista])

  const tudoPendente = pendencias.length === FONTES.length && !(lista?.usinas || []).length

  useIntervaloVisivel(() => {
    if (tudoPendente) return
    if (selecionadaRef.current) carregarDetalhe(selecionadaRef.current, true)
    else carregarLista(true)
  }, tudoPendente ? null : selecionada ? 60000 : 120000)

  const usinas = useMemo(() => lista?.usinas || [], [lista])
  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    return usinas.filter((usina) => {
      if (filtro !== 'todas' && usina.nivel !== filtro) return false
      if (marcaFiltro !== 'todas' && usina.marca !== marcaFiltro) return false
      if (!termo) return true
      return `${usina.nome} ${usina.sn} ${usina.endereco || ''} ${usina.marca}`.toLowerCase().includes(termo)
    })
  }, [usinas, busca, filtro, marcaFiltro])

  const historico = detalhe?.historico || []
  const maxKwh = Math.max(...historico.map((ponto) => Number(ponto.kwh) || 0), 0)
  const admin = userRole === 'administrador'
  const podeCredenciais = admin || Boolean(userEmpresa?.id)

  useEffect(() => {
    const novas = pendencias.filter((marca) => !pendenciasAvisadas.current.has(marca))
    if (!novas.length) return
    novas.forEach((marca) => pendenciasAvisadas.current.add(marca))
    setToast({
      id: Date.now(),
      message: novas.map((marca) => textoPendencia(marca, podeCredenciais, admin)).join(' ')
    })
  }, [pendencias, podeCredenciais, admin])
  const nomeUsina = detalhe?.usina?.nome || selecionada?.nome
  const snUsina = detalhe?.usina?.sn || selecionada?.sn
  const enderecoUsina = detalhe?.usina?.endereco || selecionada?.endereco
  const potenciaUsina = detalhe?.usina?.potenciaInstalada || selecionada?.potenciaInstalada
  const qtdAtencao = usinas.filter((item) => item.nivel === 'atencao').length
  const qtdOffline = usinas.filter((item) => item.nivel === 'offline').length
  const qtdFalha = usinas.filter((item) => item.nivel === 'falha').length
  const hojeResumo = energiaResumo(lista?.totais?.geracaoHojeKwh)
  const totalResumo = energiaResumo(lista?.totais?.geracaoTotalKwh)
  const nivelModal = detalhe?.usina?.nivel && detalhe.usina.nivel !== 'neutro' ? detalhe.usina.nivel : selecionada?.nivel
  const statusModal = detalhe?.usina?.status && detalhe.usina.status !== 'Sem status'
    ? detalhe.usina.status
    : (selecionada?.status || 'Sem status')

  function preencherCredenciais(dados) {
    setCredenciais({
      solplanet: {
        appKey: dados?.solplanet?.appKey || '',
        appSecret: dados?.solplanet?.appSecret || '',
        token: dados?.solplanet?.token || ''
      },
      goodwe: {
        appId: dados?.goodwe?.appId || '',
        appSecret: dados?.goodwe?.appSecret || ''
      }
    })
  }

  async function listarEmpresasAtivas() {
    const { data, error } = await supabase
      .from('empresa')
      .select('id, empresa')
      .eq('ativa', true)
      .order('empresa')
    if (error) throw error
    return (data || []).map((item) => ({
      id: String(item.id),
      nome: String(item.empresa || 'Empresa')
    }))
  }

  async function abrirCredenciais() {
    setCredenciaisAbertas(true)
    setAvisoCredenciais('')
    let empresas = empresasCredencial
    if (admin) {
      try {
        empresas = await listarEmpresasAtivas()
        setEmpresasCredencial(empresas)
      } catch (falha) {
        setAvisoCredenciais(textoCredencial(falha.message))
      }
    }
    const empresaInicial = admin
      ? (empresaCredencial || userEmpresa?.id || empresas[0]?.id || '')
      : (userEmpresa?.id || '')
    if (empresaInicial) setEmpresaCredencial(empresaInicial)
    try {
      const dados = await consultarCredenciais('ler', { empresaId: empresaInicial })
      if (Array.isArray(dados?.empresas) && dados.empresas.length) {
        setEmpresasCredencial(dados.empresas)
      }
      setEmpresaCredencial(dados?.empresaId || empresaInicial)
      preencherCredenciais(dados)
    } catch (falha) {
      setAvisoCredenciais(textoCredencial(falha.message))
    }
  }

  async function trocarEmpresaCredencial(empresaId) {
    setEmpresaCredencial(empresaId)
    setAvisoCredenciais('')
    if (!empresaId) return
    try {
      const dados = await consultarCredenciais('ler', { empresaId })
      preencherCredenciais(dados)
    } catch (falha) {
      setAvisoCredenciais(textoCredencial(falha.message))
    }
  }

  function alterarCredencial(marca, campo, valor) {
    setCredenciais((atual) => ({
      ...atual,
      [marca]: { ...atual[marca], [campo]: valor }
    }))
  }

  async function salvarCredenciais(event) {
    event.preventDefault()
    const empresaId = admin ? empresaCredencial : (userEmpresa?.id || '')
    if (!empresaId) {
      setAvisoCredenciais('Selecione a empresa.')
      return
    }
    const payload = abaCredencial === 'goodwe'
      ? {
        marca: 'goodwe',
        appKey: credenciais.goodwe.appId.trim(),
        appSecret: credenciais.goodwe.appSecret.trim()
      }
      : {
        marca: 'solplanet',
        appKey: credenciais.solplanet.appKey.trim(),
        appSecret: credenciais.solplanet.appSecret.trim(),
        token: ''
      }
    payload.empresaId = empresaId
    if (!payload.appKey || !payload.appSecret) {
      setAvisoCredenciais('Preencha todos os campos desta conta.')
      return
    }
    setSalvandoCredenciais(true)
    setAvisoCredenciais('')
    try {
      await consultarCredenciais('salvar', payload)
      setAvisoCredenciais('Credenciais salvas. A lista será atualizada.')
      carregarLista()
    } catch (falha) {
      setAvisoCredenciais(textoCredencial(falha.message))
    } finally {
      setSalvandoCredenciais(false)
    }
  }

  function abrirUsina(usina) {
    selecionadaRef.current = usina
    setSelecionada(usina)
    setDetalhe(null)
    carregarDetalhe(usina)
  }

  function fecharUsina() {
    selecionadaRef.current = null
    setSelecionada(null)
    setDetalhe(null)
    setErros([])
  }

  useEffect(() => {
    if (!selecionada) return undefined
    function aoTeclar(event) {
      if (event.key === 'Escape' && !credenciaisAbertas) fecharUsina()
    }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  }, [selecionada, credenciaisAbertas])

  return (
    <div className="container-fluid mon-page">
      <header className="mon-topo d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
        <div className="d-flex align-items-center gap-3">
          <div className="mon-logo" aria-hidden>
            <i className="bi bi-sun" />
          </div>
          <div>
            <span className="mon-kicker">Painel operacional</span>
            <h1>Monitoramento</h1>
          </div>
        </div>
        <div className="d-flex align-items-center flex-wrap gap-2">
          {lista?.atualizadoEm && (
            <span className="text-muted small d-none d-md-inline me-2">
              <i className="bi bi-clock me-1" />
              Atualizado às {horaCurta(lista.atualizadoEm)}
            </span>
          )}
          <button type="button" className="mon-btn mon-btn-ghost" onClick={onBack}>
            Voltar
          </button>
          {podeCredenciais && (
            <button
              type="button"
              className="mon-btn-circulo"
              onClick={async () => {
                if (admin && empresasCredencial.length === 0) {
                  try {
                    setEmpresasCredencial(await listarEmpresasAtivas())
                  } catch {
                    /* a lista abre mesmo sem o cadastro de empresas */
                  }
                }
                setAvisosAbertos(true)
              }}
              aria-label="Avisos de monitoramento"
              title="Avisos"
            >
              <i className="bi bi-bell" />
            </button>
          )}
          {podeCredenciais && (
            <button
              type="button"
              className="mon-btn-circulo"
              onClick={abrirCredenciais}
              aria-label="Credenciais de monitoramento"
              title="Credenciais"
            >
              <i className="bi bi-sliders" />
            </button>
          )}
          <button
            type="button"
            className="mon-btn mon-btn-sync"
            onClick={() => (selecionada ? carregarDetalhe(selecionada) : carregarLista())}
            disabled={carregando}
          >
            <i className={`bi bi-arrow-clockwise ${carregando ? 'mon-girando' : ''}`} />
            <span className="d-none d-sm-inline">{carregando ? 'Atualizando…' : 'Atualizar'}</span>
          </button>
        </div>
      </header>

      {podeCredenciais && (
        <MonitoramentoAvisos
          aberto={avisosAbertos}
          onFechar={() => setAvisosAbertos(false)}
          admin={admin}
          empresaInicial={userEmpresa?.id || empresasCredencial[0]?.id || ''}
          empresas={empresasCredencial}
        />
      )}

      {toast && (
        <Notification
          key={toast.id}
          message={toast.message}
          type="warning"
          duration={0}
          onClose={() => setToast(null)}
        />
      )}

      {podeCredenciais && credenciaisAbertas && (
        <div className="mon-modal-fundo" onClick={() => setCredenciaisAbertas(false)}>
          <form
            className="mon-modal"
            onClick={(event) => event.stopPropagation()}
            onSubmit={salvarCredenciais}
          >
            <div className="mon-modal-topo">
              <h2>Credenciais</h2>
              <button type="button" className="mon-modal-fechar" onClick={() => setCredenciaisAbertas(false)} aria-label="Fechar">
                ×
              </button>
            </div>
            {admin ? (
              <label>
                Empresa
                <select value={empresaCredencial} onChange={(e) => trocarEmpresaCredencial(e.target.value)}>
                  <option value="">Selecione a empresa</option>
                  {empresasCredencial.map((empresa) => (
                    <option key={empresa.id} value={empresa.id}>{empresa.nome}</option>
                  ))}
                </select>
              </label>
            ) : (
              <p>Estas credenciais ficam só na empresa {userEmpresa?.empresa || 'da sua conta'}.</p>
            )}
            <div className="mon-abas" role="tablist">
              <button
                type="button"
                className={abaCredencial === 'solplanet' ? 'is-active' : ''}
                onClick={() => { setAbaCredencial('solplanet'); setAvisoCredenciais('') }}
              >
                Solplanet
              </button>
              <button
                type="button"
                className={abaCredencial === 'goodwe' ? 'is-active' : ''}
                onClick={() => { setAbaCredencial('goodwe'); setAvisoCredenciais('') }}
              >
                GoodWe
              </button>
            </div>
            {abaCredencial === 'solplanet' ? (
              <>
                <p>
                  Use o mesmo e-mail e a mesma senha de internation-pro-cloud.solplanet.net.
                  A consulta entra nessa conta e lê as usinas em /api/overview/getPlantList.
                </p>
                <label>
                  E-mail ou telefone
                  <input
                    value={credenciais.solplanet.appKey}
                    onChange={(e) => alterarCredencial('solplanet', 'appKey', e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                </label>
                <label>
                  Senha
                  <input
                    type="password"
                    value={credenciais.solplanet.appSecret}
                    onChange={(e) => alterarCredencial('solplanet', 'appSecret', e.target.value)}
                    autoComplete="new-password"
                    spellCheck={false}
                  />
                </label>
              </>
            ) : (
              <>
                <p>AppId e AppSecret da conta organização no portal da GoodWe.</p>
                <label>
                  AppId
                  <input
                    value={credenciais.goodwe.appId}
                    onChange={(e) => alterarCredencial('goodwe', 'appId', e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                </label>
                <label>
                  AppSecret
                  <input
                    type="password"
                    value={credenciais.goodwe.appSecret}
                    onChange={(e) => alterarCredencial('goodwe', 'appSecret', e.target.value)}
                    autoComplete="new-password"
                    spellCheck={false}
                  />
                </label>
              </>
            )}
            {avisoCredenciais && <p className="mon-nota">{avisoCredenciais}</p>}
            <button type="submit" className="mon-btn" disabled={salvandoCredenciais}>
              {salvandoCredenciais ? 'Salvando…' : 'Salvar'}
            </button>
          </form>
        </div>
      )}

      {!selecionada && erros.length > 0 && (
        <section className="mon-erro" role="alert">
          {erros.map((item) => (
            <p key={item.marca}>
              <strong>{item.marca}</strong>
              {item.texto}
            </p>
          ))}
        </section>
      )}

      {lista && (
        <>
          <section className="row g-3 mb-4 mon-kpis">
            <KpiCard rotulo="Total usinas" valor={numeroPt(lista.totais?.usinas, 0)} icone="bi-buildings" tom="emerald" />
            <KpiCard rotulo="Normais" valor={numeroPt(lista.totais?.normais, 0)} icone="bi-check-circle" tom="success" />
            <KpiCard rotulo="Atenção" valor={numeroPt(qtdAtencao, 0)} icone="bi-exclamation-triangle" tom="warning" />
            <KpiCard rotulo="Offline" valor={numeroPt(qtdOffline, 0)} icone="bi-wifi-off" tom="offline" />
            <KpiCard rotulo="Com falha" valor={numeroPt(qtdFalha, 0)} icone="bi-x-circle" tom="danger" />
            <KpiCard rotulo="Geração hoje" valor={hojeResumo.valor} unidade={hojeResumo.unidade} icone="bi-lightning-charge" tom="hoje" />
            <KpiCard rotulo="Acumulado" valor={totalResumo.valor} unidade={totalResumo.unidade} icone="bi-graph-up" tom="plain" />
          </section>

          {lista.aviso && <p className="mon-nota">{lista.aviso}</p>}

          <div className="mon-toolbar">
            <div className="row g-3 align-items-center">
              <div className="col-12 col-lg-4">
                <div className="mon-busca">
                  <i className="bi bi-search text-muted" aria-hidden />
                  <input
                    type="search"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar por nome, número de série ou endereço..."
                    aria-label="Buscar usina"
                  />
                </div>
              </div>
              <div className="col-12 col-lg-8 d-flex flex-wrap gap-2 justify-content-lg-end align-items-center">
                <div className="d-flex gap-1 flex-wrap" role="tablist" aria-label="Filtrar por marca">
                  {MARCAS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`mon-filtro ${marcaFiltro === item.id ? 'is-active' : ''}`}
                      onClick={() => setMarcaFiltro(item.id)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                <div className="vr mx-1 d-none d-lg-block" />
                <div className="d-flex gap-1 flex-wrap" role="tablist" aria-label="Filtrar por status">
                  {FILTROS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`mon-filtro ${filtro === item.id ? 'is-active' : ''}`}
                      onClick={() => setFiltro(item.id)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mon-tabela-caixa">
            <div className="mon-desktop table-responsive">
              <table className="table mon-tabela mb-0">
                <thead>
                  <tr>
                    <th>Usina</th>
                    <th>Status</th>
                    <th className="text-end">Potência agora</th>
                    <th className="text-end">Geração hoje</th>
                    <th className="text-end">Acumulado</th>
                    <th className="text-end">Instalada</th>
                    <th>Endereço</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {visiveis.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-5">
                        <i className="bi bi-folder2-open mon-vazio-icone" aria-hidden />
                        <p className="text-muted fw-medium m-0">
                          {usinas.length === 0
                            ? 'Nenhuma usina encontrada nas contas já ligadas.'
                            : 'Nenhuma usina corresponde à busca.'}
                        </p>
                      </td>
                    </tr>
                  ) : visiveis.map((usina) => (
                    <tr key={`${usina.marca}-${usina.id}`} onClick={() => abrirUsina(usina)}>
                      <td>
                        <div className="fw-bold text-dark mb-1">{usina.nome}</div>
                        <MarcaSelo marca={usina.marca} />
                      </td>
                      <td><StatusPill nivel={usina.nivel} texto={usina.status} /></td>
                      <td className="text-end fw-semibold text-nowrap">{numeroPt(usina.potenciaKw, 2)} kW</td>
                      <td className="text-end fw-semibold text-nowrap mon-hoje">{numeroPt(usina.geracaoHojeKwh)} kWh</td>
                      <td className="text-end text-muted text-nowrap">{numeroPt(usina.geracaoTotalKwh, 0)} kWh</td>
                      <td className="text-end fw-bold text-nowrap">{usina.potenciaInstalada || '—'}</td>
                      <td className="text-muted small mon-endereco" title={usina.endereco || ''}>
                        <i className="bi bi-geo-alt me-1" aria-hidden />
                        {usina.endereco || '—'}
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-light border rounded-pill px-3 fw-semibold"
                          onClick={(event) => { event.stopPropagation(); abrirUsina(usina) }}
                        >
                          <i className="bi bi-eye me-1" aria-hidden />
                          Detalhes
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mon-mobile p-3">
              {visiveis.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-folder2-open mon-vazio-icone" aria-hidden />
                  <p className="m-0">
                    {usinas.length === 0
                      ? 'Nenhuma usina encontrada nas contas já ligadas.'
                      : 'Nenhuma usina corresponde à busca.'}
                  </p>
                </div>
              ) : visiveis.map((usina) => (
                <button
                  key={`${usina.marca}-${usina.id}`}
                  type="button"
                  className="mon-mobile-card"
                  onClick={() => abrirUsina(usina)}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div className="text-start">
                      <MarcaSelo marca={usina.marca} />
                      <h6 className="fw-bold text-dark m-0 mt-1">{usina.nome}</h6>
                    </div>
                    <StatusPill nivel={usina.nivel} texto={usina.status} />
                  </div>
                  <p className="text-muted small mb-3 text-start">
                    <i className="bi bi-geo-alt me-1" aria-hidden />
                    {usina.endereco || 'Sem endereço informado'}
                  </p>
                  <div className="row bg-light rounded-3 p-2 text-center g-1 mb-2 mon-mobile-nums">
                    <div className="col-4 border-end">
                      <span>Agora</span>
                      <strong>{numeroPt(usina.potenciaKw, 2)} kW</strong>
                    </div>
                    <div className="col-4 border-end">
                      <span>Hoje</span>
                      <strong className="mon-hoje">{numeroPt(usina.geracaoHojeKwh)} kWh</strong>
                    </div>
                    <div className="col-4">
                      <span>Instalada</span>
                      <strong>{usina.potenciaInstalada || '—'}</strong>
                    </div>
                  </div>
                  <span className="btn btn-sm btn-outline-dark w-100 rounded-pill fw-semibold">
                    <i className="bi bi-eye me-1" aria-hidden />
                    Ver detalhes
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {selecionada && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(22, 48, 40, 0.45)' }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mon-usina-titulo"
          onClick={fecharUsina}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable modal-fullscreen-md-down"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-content mon-detalhe">
              <div className="modal-body">
                <div className="mon-detalhe-topo border-bottom pb-3 mb-4">
                  <div className="mon-detalhe-titulo">
                    <MarcaSelo marca={selecionada.marca} />
                    <h2 id="mon-usina-titulo" className="fw-bold text-dark h3 mt-2 mb-1">{nomeUsina}</h2>
                    <p className="text-muted small mb-0">
                      <i className="bi bi-geo-alt me-1" aria-hidden />
                      {enderecoUsina || 'Sem endereço informado'}
                      {potenciaUsina ? <> · <strong className="text-dark">{potenciaUsina}</strong></> : null}
                      {snUsina ? <> · SN {snUsina}</> : null}
                    </p>
                  </div>
                  <div className="mon-detalhe-acoes">
                    <StatusPill nivel={nivelModal} texto={statusModal} />
                    <button type="button" className="btn-close" onClick={fecharUsina} aria-label="Fechar" />
                  </div>
                </div>

                {carregando && !detalhe && (
                  <p className="mon-vazio mb-0">Carregando detalhes da usina…</p>
                )}
                {erros.length > 0 && (
                  <section className="mon-erro mb-3" role="alert">
                    {erros.map((item) => (
                      <p key={item.marca}>
                        <strong>{item.marca}</strong>
                        {item.texto}
                      </p>
                    ))}
                  </section>
                )}
                {detalhe && (
                  <>
                    <div className="row g-3 mb-4">
                      <div className="col-6 col-md-3">
                        <div className="mon-metrica">
                          <span>Potência agora</span>
                          <strong>{numeroPt(detalhe.resumo?.potenciaKw, 2)} kW</strong>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="mon-metrica">
                          <span>Geração hoje</span>
                          <strong className="mon-hoje">{numeroPt(detalhe.resumo?.geracaoHojeKwh)} kWh</strong>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="mon-metrica">
                          <span>Acumulada</span>
                          <strong>{numeroPt(detalhe.resumo?.geracaoTotalKwh, 0)} kWh</strong>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="mon-metrica">
                          <span>Inversores</span>
                          <strong>{detalhe.resumo?.gerando ?? 0}/{detalhe.resumo?.inversores ?? 0}</strong>
                        </div>
                      </div>
                    </div>

                    <div className="mon-painel-grafico mb-4">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <h3 className="h6 fw-bold text-dark m-0">
                          <i className="bi bi-bar-chart-fill me-2 mon-hoje" aria-hidden />
                          Geração dos últimos 30 dias
                        </h3>
                        <span className="badge bg-light text-muted border">kWh/dia</span>
                      </div>
                      <GraficoGeracao historico={historico} maxKwh={maxKwh} />
                    </div>

                    {Array.isArray(detalhe.eventos) && (
                      <div className="mon-painel-grafico mb-4">
                        <h3 className="h6 fw-bold text-dark mb-3">
                          <i className="bi bi-exclamation-diamond me-2 mon-hoje" aria-hidden />
                          Falhas e atenção dos últimos 7 dias
                        </h3>
                        {detalhe.eventos.length === 0 ? (
                          <p className="mon-vazio mb-0">Nenhum registro de falha ou atenção neste período.</p>
                        ) : (
                          <ul className="mon-eventos">
                            {detalhe.eventos.map((evento) => (
                              <li key={evento.id}>
                                <div className="d-flex justify-content-between align-items-start gap-2">
                                  <strong>{evento.descricao}</strong>
                                  <StatusPill nivel={evento.nivel} texto={evento.nivel === 'atencao' ? 'Atenção' : 'Falha'} />
                                </div>
                                <p>
                                  {evento.quando}
                                  {evento.serie ? ` · SN ${evento.serie}` : ''}
                                  {evento.codigo && evento.codigo !== evento.descricao ? ` · ${evento.codigo}` : ''}
                                </p>
                                <small>{evento.recuperadoEm ? `Recuperado em ${evento.recuperadoEm}` : 'Ainda em aberto'}</small>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                    <div className="mon-painel-grafico">
                      <h3 className="h6 fw-bold text-dark mb-3">
                        <i className="bi bi-cpu me-2 mon-hoje" aria-hidden />
                        Inversores da usina
                      </h3>
                      {(detalhe.inversores || []).length === 0 ? (
                        <p className="mon-vazio mb-0">Nenhum inversor vinculado a esta usina.</p>
                      ) : (
                        <>
                          <div className="mon-inv-cards">
                            {detalhe.inversores.map((inv) => {
                              const extras = resumoInversor(inv)
                              return (
                                <article key={inv.id || inv.sn} className="mon-inv-card">
                                  <div className="d-flex justify-content-between align-items-start gap-2">
                                    <div>
                                      <strong>{inv.nome}</strong>
                                      <small>{inv.sn ? `SN ${inv.sn}` : 'Sem número de série'}</small>
                                    </div>
                                    <StatusPill nivel={inv.nivel} texto={inv.status} />
                                  </div>
                                  <div className="row g-2 mon-mobile-nums text-center">
                                    <div className="col-6">
                                      <span>Potência</span>
                                      <strong>{numeroPt(inv.potenciaKw, 2)} kW</strong>
                                    </div>
                                    <div className="col-6">
                                      <span>Hoje</span>
                                      <strong className="mon-hoje">{numeroPt(inv.geracaoHojeKwh)} kWh</strong>
                                    </div>
                                    <div className="col-6">
                                      <span>Acumulado</span>
                                      <strong>{numeroPt(inv.geracaoTotalKwh, 0)} kWh</strong>
                                    </div>
                                    <div className="col-6">
                                      <span>Leitura</span>
                                      <strong>{inv.atualizadoEm ? horaLocal(inv.atualizadoEm) : '—'}</strong>
                                    </div>
                                  </div>
                                  {extras.length > 0 && <p>{extras.join(' · ')}</p>}
                                  {inv.strings?.length > 0 && (
                                    <p>
                                      {inv.strings.map((mppt) => `MPPT ${mppt.indice}: ${numeroPt(mppt.tensao, 0)} V · ${numeroPt(mppt.corrente, 1)} A`).join(' · ')}
                                    </p>
                                  )}
                                </article>
                              )
                            })}
                          </div>
                          <div className="table-responsive mon-inv-tabela">
                            <table className="table align-middle mb-0 mon-tabela-inv">
                              <thead>
                                <tr>
                                  <th>Modelo / nº série</th>
                                  <th>Status</th>
                                  <th>Potência</th>
                                  <th>Hoje</th>
                                  <th>Acumulado</th>
                                  <th>Última leitura</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detalhe.inversores.map((inv) => {
                                  const extras = resumoInversor(inv)
                                  return (
                                    <tr key={inv.id || inv.sn}>
                                      <td>
                                        <div className="fw-bold text-dark">{inv.nome}</div>
                                        <small className="text-muted">{inv.sn ? `SN ${inv.sn}` : 'Sem número de série'}</small>
                                        {extras.length > 0 && <div className="text-muted small">{extras.join(' · ')}</div>}
                                        {inv.strings?.length > 0 && (
                                          <div className="text-muted small">
                                            {inv.strings.map((mppt) => `MPPT ${mppt.indice}: ${numeroPt(mppt.tensao, 0)} V · ${numeroPt(mppt.corrente, 1)} A`).join(' · ')}
                                          </div>
                                        )}
                                      </td>
                                      <td><StatusPill nivel={inv.nivel} texto={inv.status} /></td>
                                      <td className="fw-semibold text-nowrap">{numeroPt(inv.potenciaKw, 2)} kW</td>
                                      <td className="text-nowrap">{numeroPt(inv.geracaoHojeKwh)} kWh</td>
                                      <td className="text-nowrap">{numeroPt(inv.geracaoTotalKwh, 0)} kWh</td>
                                      <td className="text-muted small text-nowrap">{inv.atualizadoEm ? horaLocal(inv.atualizadoEm) : '—'}</td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {carregando && !lista && !detalhe && pendencias.length === 0 && (
        <p className="mon-vazio">Carregando usinas…</p>
      )}
    </div>
  )
}
